import { useNavigate } from 'react-router-dom';

import { ButtonBase } from '@mui/material';

import { useRecentlyViewedEmployeesGet } from '@sbv-clients/data-access-api';
import {
  Badge,
  Box,
  Card,
  CircularProgress,
  GridEmptyState,
  LetterAvatar,
  useTheme,
} from '@sbv-clients/ui-shared';

import { CardTitle } from './components/card-head';
import { LineIcon } from './components/line-icon';
import { useCardSx } from './components/use-card-sx';
import { ViewAllButton } from './components/view-all-button';

const EmployeeRow = ({
  name,
  pictureUrl,
  isActive,
  onClick,
}: {
  name: string;
  pictureUrl: string;
  isActive: boolean;
  onClick: () => void;
}) => {
  const theme = useTheme();
  const inactive = !isActive;

  // The name comes with the ID in it ("Name (#123)"); show the ID muted.
  const match = name.match(/^(.*?)\s*\((#[^)]+)\)\s*$/);
  const displayName = match ? match[1] : name;
  const displayId = match ? match[2] : '';

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        justifyContent: 'flex-start',
        gap: '12px',
        minWidth: 0,
        height: 52,
        padding: '0 8px',
        borderRadius: '10px',
        textAlign: 'left',
        opacity: inactive ? 0.7 : 1,
        '&:hover': { background: theme.custom?.hoverBg },
      }}
    >
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        variant="dot"
        sx={{
          '& .MuiBadge-badge': {
            right: 3,
            bottom: 4,
            backgroundColor: inactive
              ? theme.background.errorDot
              : theme.colors.success.main,
            border: `1.5px solid ${theme.background.white}`,
            height: '9px',
            width: '9px',
            minWidth: 0,
            borderRadius: 360,
          },
        }}
      >
        <LetterAvatar
          inactive={inactive}
          name={name}
          src={pictureUrl}
          size="sm"
          sx={{
            height: 34,
            width: 34,
            flexShrink: 0,
            fontSize: '12px',
            background: inactive
              ? theme.background.lightGrey
              : theme.background.lightPurple,
            color: inactive
              ? theme.background.darkGrey2
              : theme.colors.primary.main,
          }}
        />
      </Badge>
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '14px',
          fontWeight: 600,
          color: inactive
            ? theme.background.darkGrey2
            : theme.custom?.brandNavy,
        }}
      >
        {displayName}
        {displayId && (
          <Box
            component="span"
            sx={{
              ml: '6px',
              fontSize: '12.5px',
              fontWeight: 400,
              color: theme.custom?.tableHeaderText,
            }}
          >
            {displayId}
          </Box>
        )}
      </Box>
      <Box
        component="span"
        sx={{ display: 'flex', color: theme.custom?.iconButton }}
      >
        <LineIcon name="chevron" />
      </Box>
    </ButtonBase>
  );
};

export const RecentlyViewedCard = () => {
  const theme = useTheme();
  const cardSx = useCardSx();
  const navigate = useNavigate();
  const { data: recentEmployees = [], isLoading } =
    useRecentlyViewedEmployeesGet();

  return (
    <Card sx={cardSx} elevation={0}>
      <CardTitle
        title="Recently viewed employees"
        right={
          <ViewAllButton
            onClick={() =>
              navigate('/employees', { state: { skipPersistedFilters: true } })
            }
          />
        }
      />
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : !recentEmployees.length ? (
        <GridEmptyState icon="people" title="No recently viewed employees" />
      ) : (
        <Box
          sx={{
            mt: '8px',
            mb: '-6px',
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
            },
            columnGap: '24px',
            '& > button': {
              borderTop: `1px solid ${theme.custom?.tableRowLine}`,
              borderRadius: 0,
            },
            '& > button:nth-of-type(1)': { borderTop: 'none' },
            '@media (min-width: 900px)': {
              '& > button:nth-of-type(2)': { borderTop: 'none' },
            },
          }}
        >
          {recentEmployees.map((emp) => (
            <EmployeeRow
              key={emp.pkEmployeeId}
              name={emp.displayName ?? ''}
              pictureUrl={emp.pictureUrl ?? ''}
              isActive={emp.isActive ?? false}
              onClick={() =>
                navigate(`/employees?employeeId=${emp.pkEmployeeId}`)
              }
            />
          ))}
        </Box>
      )}
    </Card>
  );
};
